import React, { useEffect, useState } from 'react';
import { SlSidebar, SlSidebarSectionContainer, SlSidebarTitle } from 'src/components/BeaconV2Components/SlSidebar';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Box } from '@mui/system';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import BrandSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/BrandSidebarFilter';
import CategorySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/CategorySidebarFilter';
import SubcategorySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/SubcategorySidebarFilter';
import TopicSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/TopicSidebarFilter';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { TopicFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  StorageSource,
  useTopicBuilderActions,
  useTopicBuilderFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { SegmentBuilderBulkUploadModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/SegmentBuilderBulkUploadModal';
import { TopicWithOwner } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import TopicOwner from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/TopicOwner';
import SidebarInput from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/SidebarInput';
import { useTopicMutation } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useTopicMutation';

/**
 * Filter group for the Segment Builder sidebar.
 */
const TopicBuilderSidebarFilters = ({ readOnly }: { readOnly: boolean }) => {
  return (
    <>
      {/* Include - Phrase */}
      <Box sx={{ marginTop: '32px' }}>
        <TopicSidebarFilter
          readOnly={readOnly}
          filterKey={TopicFilter.IncludedKeywordPhrases}
          title="Include - Phrase"
          sidebarInputProps={{ placeholder: 'Keyword phrase to include' }}
        />
      </Box>

      {/* Include - Exact */}
      <TopicSidebarFilter
        readOnly={readOnly}
        filterKey={TopicFilter.IncludedKeywords}
        title="Include - Exact"
        sidebarInputProps={{ placeholder: 'Exact keyword to include' }}
      />

      {/* Exclude - Phrase */}
      <TopicSidebarFilter
        readOnly={readOnly}
        filterKey={TopicFilter.ExcludedKeywordPhrases}
        title="Exclude - Phrase"
        sidebarInputProps={{ placeholder: 'Keyword phrase to exclude' }}
      />

      {/* Exclude - Exact */}
      <TopicSidebarFilter
        readOnly={readOnly}
        filterKey={TopicFilter.ExcludedKeywords}
        title="Exclude - Exact"
        sidebarInputProps={{ placeholder: 'Exact keyword to exclude' }}
      />

      {/* Brands */}
      <BrandSidebarFilter title="Brands" sidebarInputProps={{ placeholder: 'Refine by brands' }} readOnly={readOnly} />

      {/* Categories */}
      <Box sx={{ marginTop: '32px' }}>
        <CategorySidebarFilter
          title="Categories"
          sidebarInputProps={{ placeholder: 'Refine by categories' }}
          readOnly={readOnly}
        />
      </Box>

      {/* Subcategories */}
      <Box sx={{ marginTop: '32px' }}>
        <SubcategorySidebarFilter
          title="Subcategories"
          sidebarInputProps={{ placeholder: 'Refine by subcategories' }}
          readOnly={readOnly}
        />
      </Box>
    </>
  );
};

/**
 * Memoized version of the TopicBuilderSidebarFilters component to avoid unnecessary rerenders
 */
const MemoizedTopicFilters = React.memo(TopicBuilderSidebarFilters);

export const TopicBuilderSidebarV2 = ({
  source,
  readOnly,
  topic,
  editMode
}: {
  source?: StorageSource;
  readOnly?: boolean;
  topic?: TopicWithOwner;
  editMode?: boolean;
}) => {
  const theme = useStacklineTheme();

  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);

  const { filters } = useTopicBuilderFilters();
  const { setFilters, storage } = useTopicBuilderActions();

  const { mutateAsync: saveTopic, isLoading: isSaving } = useTopicMutation();

  useEffect(() => {
    // Populate filter state via local storage
    storage.hydrate(source);

    // In some cases, we navigate from one topic builder to another. In this case, we might not want to clear the local storage on unmount.
    return () => {
      storage.clear(source);
    };
  }, []);

  const [name, setName] = useState('');

  const handleSave = () => {
    saveTopic({
      name,
      topic,
      filters,
      edit: editMode
    });
  };

  return (
    <SlSidebar gap={0} sx={{ width: 'auto' }}>
      <SlSidebarSectionContainer gap={0}>
        {topic ? (
          <Box sx={{ marginBottom: '48px' }}>
            <TopicOwner
              createdAt={topic?.createdAt}
              firstName={topic?.owner?.firstName}
              lastName={topic?.owner?.lastName}
              userId={topic?.ownedByUserId}
            />
          </Box>
        ) : (
          <Text sx={{ marginBottom: '32px', marginTop: '54px' }} variant="h4">
            Filter
          </Text>
        )}

        <SlSidebarTitle>Name</SlSidebarTitle>
        <SidebarInput
          style={{ marginTop: '8px' }}
          readOnly={readOnly}
          value={readOnly && topic?.topicName ? topic?.topicName : name}
          onChange={(e) => setName(e.target.value)}
          placeholder={topic?.topicName ?? 'Name your topic'}
        />
      </SlSidebarSectionContainer>

      <MemoizedTopicFilters readOnly={readOnly} />

      <SegmentBuilderBulkUploadModal
        isTopic
        open={bulkUploadModalOpen}
        handleClose={() => setBulkUploadModalOpen(false)}
      />

      {!readOnly && (
        <Flex flexDirection="column" marginTop="64px" gap={16}>
          <SlButton onClick={handleSave} disabled={(!editMode && !name.trim()) || isSaving} variant="contained">
            {isSaving ? 'Saving...' : 'Save'}
          </SlButton>
          <SlButton
            onClick={() => setBulkUploadModalOpen(true)}
            variant="tertiary"
          >
            Bulk Upload
          </SlButton>
          <SlButton onClick={() => setFilters(({ clearAll }) => clearAll())}>Clear Filters</SlButton>
        </Flex>
      )}
    </SlSidebar>
  );
};
